import type { Role } from './auth';
import { apiFetch } from './client';
import type { Page } from './discover';

/**
 * Administration (Phase 24).
 *
 * Every call here answers 403 to anybody without the role, whatever this file
 * does. The admin area asks `/me` for the role so it can avoid offering a
 * screen that would only refuse — never as the thing that decides (Rule 7).
 */

export type { Role };

export interface Overview {
  counts: Record<string, number>;
  not_yet_available: { key: string; label: string; note: string }[];
}

export interface AdminUser {
  id: string;
  handle: string;
  display_name: string;
  email: string;
  role: Role;
  is_active: boolean;
  is_email_verified: boolean;
  date_joined: string;
  story_count: number;
  /** So the screen can say why some buttons are not offered. */
  is_me: boolean;
}

export interface Limit {
  key: string;
  label: string;
  description: string;
  kind: 'int' | 'bool';
  minimum: number;
  maximum: number;
  /** What it protects, so a person changing it knows what they are trading. */
  guards: string;
  value: number | boolean;
  is_overridden: boolean;
}

export interface AdminCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  position: number;
  is_active: boolean;
  story_count: number;
}

export interface AdminTag {
  id: string;
  name: string;
  slug: string;
  story_count: number;
  created_at: string;
}

export interface AdminTemplate {
  key: string;
  name: string;
  description: string;
  is_enabled: boolean;
  position: number;
}

export interface AuditEntry {
  id: string;
  actor: { handle: string; exists: boolean };
  action: string;
  action_label: string;
  target: string;
  before: string;
  after: string;
  reason: string;
  created_at: string;
}

function send<T>(path: string, method: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

export const getOverview = (): Promise<Overview> => apiFetch<Overview>('/admin/overview');

/**
 * What the background is doing (Phase 32).
 *
 * `count: null` and a queue depth of `null` mean *we could not look*, which is
 * deliberately not the same as zero: a queue reported empty because nothing
 * answered is worse than one reported unknown.
 */
export interface BackgroundReport {
  workers: { count: number | null; queues: string[] };
  queues: Record<string, number | null>;
  work: {
    pending_versions: number;
    late_versions: number;
    oldest_pending_minutes: number;
    late_after_minutes: number;
  };
}

export const getBackground = (): Promise<BackgroundReport> =>
  apiFetch<BackgroundReport>('/admin/background');

/**
 * What this instance has been spending (Phase 33).
 *
 * The same numbers a scraper reads from `/metrics`, digested: the screen and
 * the scrape are rendered from one set of stored series, so they cannot
 * disagree about what the instance has been doing.
 */
export interface MetricsDigest {
  requests: { total: number; by_status: Record<string, number>; error_ratio: number };
  slowest_routes: { name: string; calls: number; average_ms: number }[];
  tasks: {
    by_outcome: Record<string, Record<string, number>>;
    slowest: { name: string; calls: number; average_ms: number }[];
  };
  analyses: { computed: number; remembered: number; cache_ratio: number };
}

export const getMetrics = (): Promise<MetricsDigest> => apiFetch<MetricsDigest>('/admin/metrics');

export function listUsers(query: { q?: string; role?: string; active?: string } = {}) {
  const search = new URLSearchParams();
  if (query.q) search.set('q', query.q);
  if (query.role) search.set('role', query.role);
  if (query.active) search.set('active', query.active);
  const suffix = search.toString();
  return apiFetch<Page<AdminUser>>(`/admin/users${suffix ? `?${suffix}` : ''}`);
}

export const setRole = (handle: string, role: Role, reason = ''): Promise<AdminUser> =>
  send<AdminUser>(`/admin/users/${handle}/role`, 'PUT', { role, reason });

export const setActive = (handle: string, isActive: boolean, reason = ''): Promise<AdminUser> =>
  send<AdminUser>(`/admin/users/${handle}/active`, 'PUT', { is_active: isActive, reason });

export const listLimits = (): Promise<{ limits: Limit[] }> =>
  apiFetch<{ limits: Limit[] }>('/admin/limits');

export const setLimit = (key: string, value: string): Promise<Limit> =>
  send<Limit>(`/admin/limits/${key}`, 'PUT', { value });

export const resetLimit = (key: string): Promise<Limit> =>
  apiFetch<Limit>(`/admin/limits/${key}`, { method: 'DELETE' });

export const listCategories = (): Promise<{ results: AdminCategory[] }> =>
  apiFetch<{ results: AdminCategory[] }>('/admin/categories');

export interface CategoryInput {
  name: string;
  description?: string;
  position?: number;
  is_active?: boolean;
}

export const createCategory = (input: CategoryInput): Promise<AdminCategory> =>
  send<AdminCategory>('/admin/categories', 'POST', input);

export const updateCategory = (slug: string, input: CategoryInput): Promise<AdminCategory> =>
  send<AdminCategory>(`/admin/categories/${slug}`, 'PUT', input);

export const listTags = (q = ''): Promise<Page<AdminTag>> =>
  apiFetch<Page<AdminTag>>(`/admin/tags${q ? `?q=${encodeURIComponent(q)}` : ''}`);

export const renameTag = (slug: string, name: string): Promise<AdminTag> =>
  send<AdminTag>(`/admin/tags/${slug}`, 'PUT', { name });

export const deleteTag = (slug: string, reason = ''): Promise<void> =>
  send<void>(`/admin/tags/${slug}`, 'DELETE', { reason });

export const listTemplates = (): Promise<{ results: AdminTemplate[] }> =>
  apiFetch<{ results: AdminTemplate[] }>('/admin/templates');

export const setTemplate = (key: string, isEnabled: boolean): Promise<AdminTemplate> =>
  send<AdminTemplate>(`/admin/templates/${key}`, 'PUT', { is_enabled: isEnabled });

export const listAudit = (query: { action?: string; actor?: string } = {}) => {
  const search = new URLSearchParams();
  if (query.action) search.set('action', query.action);
  if (query.actor) search.set('actor', query.actor);
  const suffix = search.toString();
  return apiFetch<Page<AuditEntry>>(`/admin/audit${suffix ? `?${suffix}` : ''}`);
};

export const listAuditActions = (): Promise<{ actions: { value: string; label: string }[] }> =>
  apiFetch<{ actions: { value: string; label: string }[] }>('/admin/audit/actions');

/** Moderation: the Featured shelf. A moderator's, not an administrator's. */
export const setFeatured = (postId: string, isFeatured: boolean, reason = '') =>
  send<{ id: string; featured_at: string | null }>(`/moderation/stories/${postId}/feature`, 'PUT', {
    is_featured: isFeatured,
    reason,
  });
